import React from 'react';
import { Loader2, ShieldCheck, Mail, KeyRound, ExternalLink, PenLine, Info } from 'lucide-react';
import { publicPrestationDevisApi } from '../../lib/api';

export interface ContexteSignature {
    cgv: { file_name: string; deposees_le: string | null; url: string } | null;
    retractation_days: number;
    // Signer engage-t-il le client ? (réglage de l'établissement ; absent = oui, comportement historique)
    engage?: boolean;
    particulier: boolean;
    nom_par_defaut: string;
    email: string;
    // Propre au devis (numéro affiché nulle part dans ce composant) : absent pour un contrat d'abonnement.
    devis_number?: string;
    // Texte exact de la case à cocher, construit par le serveur : c'est cette chaîne-là, mot pour mot,
    // qui est recopiée dans le certificat et le fichier de preuve.
    mention: string;
}

/**
 * Signature en ligne du devis (lot S1, 2026-09-22) — niveau « simple avec dossier de preuve » : le client
 * coche la mention d'acceptation, reçoit un code à 6 chiffres sur l'adresse qui a reçu le devis, le saisit ;
 * le serveur scelle le PDF (page de certificat + empreintes) et prévient tout le monde.
 *
 * Une seule case obligatoire (simplification demandée par Emeni le 2026-09-22) : acceptation du devis,
 * réception avant exécution, CGV et information sur la rétractation sont réunies dans une même mention.
 * « Bon pour accord » n'est pas une obligation légale (cf. construireMentionSignature côté serveur). La
 * demande d'exécution anticipée reste une case à part : c'est une demande expresse, et facultative.
 *
 * Cases NON précochées, volontairement : c'est ce qui rend l'acceptation opposable au client.
 */
// `api`/`documentLabel` : généralisés (2026-09-26) pour être réutilisés par la signature du contrat
// d'abonnement, qui doit suivre exactement la même règle que le devis (même page, mêmes routes derrière,
// juste un jeton et un libellé différents) — jamais un mécanisme séparé et moins sûr (dépôt de fichier).
export const SignatureDevisEnLigne: React.FC<{
    token: string; ctx: ContexteSignature; onSigned: (receivedAt: string, bookingUrl?: string | null) => void;
    api?: { requestSignatureCode: typeof publicPrestationDevisApi.requestSignatureCode; confirmSignature: typeof publicPrestationDevisApi.confirmSignature };
    documentLabel?: string;
}> = ({ token, ctx, onSigned, api = publicPrestationDevisApi, documentLabel = 'devis' }) => {
    const [nom, setNom] = React.useState(ctx.nom_par_defaut || '');
    const [mentionOk, setMentionOk] = React.useState(false);
    const [executionAnticipee, setExecutionAnticipee] = React.useState(false);
    const [etape, setEtape] = React.useState<'mentions' | 'code'>('mentions');
    const [code, setCode] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [erreur, setErreur] = React.useState('');
    const [codeInfo, setCodeInfo] = React.useState<{ email: string; sent_at: string } | null>(null);
    const [renvoiDans, setRenvoiDans] = React.useState(0);

    React.useEffect(() => {
        if (renvoiDans <= 0) return;
        const t = setTimeout(() => setRenvoiDans((n) => n - 1), 1000);
        return () => clearTimeout(t);
    }, [renvoiDans]);

    const engage = ctx.engage !== false;
    const avecRetractation = ctx.retractation_days > 0;
    const pret = nom.trim().length >= 2 && mentionOk;

    const demanderCode = async () => {
        if (!pret || busy) return;
        setBusy(true); setErreur('');
        try {
            const r: any = await api.requestSignatureCode(token, nom.trim());
            if (r?.error) { setErreur(r.error); return; }
            setCodeInfo({ email: r.email, sent_at: r.sent_at });
            setEtape('code'); setCode(''); setRenvoiDans(60);
        } catch (e: any) {
            setErreur(e?.message || "Le code n'a pas pu être envoyé.");
        } finally { setBusy(false); }
    };

    const signer = async () => {
        if (code.replace(/\D/g, '').length !== 6 || busy) return;
        setBusy(true); setErreur('');
        try {
            const r: any = await api.confirmSignature(token, {
                code: code.replace(/\D/g, ''),
                mention_acceptee: mentionOk,
                retractation_waiver: executionAnticipee,
            });
            if (r?.error) { setErreur(r.error); return; }
            onSigned(r.received_at, r.booking_url || null);
        } catch (e: any) {
            setErreur(e?.message || 'La signature a échoué.');
        } finally { setBusy(false); }
    };

    const champ = 'w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500';

    return (
        <div className="space-y-4">
            {etape === 'mentions' ? (
                <div className="space-y-3">
                    {/* Ce qui se passe quand on signe, dit AVANT de cocher quoi que ce soit : l'engagement, puis
                        le code par email — sinon le client découvre l'étape du code au moment de cliquer. */}
                    <div className="flex items-start gap-2.5 p-3 rounded-xl bg-surface-soft border border-border">
                        <ShieldCheck size={18} className="text-brand-600 dark:text-brand-400 shrink-0 mt-0.5" />
                        <div className="space-y-1">
                            <p className="text-sm font-bold text-content">{engage ? `Signer ce ${documentLabel} vous engage` : `Ce ${documentLabel} est sans engagement`}</p>
                            <p className="text-xs text-content-muted leading-relaxed">
                                {engage
                                    ? `En signant, vous acceptez le ${documentLabel} et les prestations qu'il décrit.`
                                    : `Signer ne vous engage en rien : vous validez le prix et vous pourrez ensuite fixer votre rendez-vous.`} Pour confirmer que c'est bien vous,
                                un <span className="font-bold text-content">code à 6 chiffres</span> vous sera envoyé par email sur <span className="font-bold text-content">{ctx.email}</span>.
                                Votre {documentLabel} signé, avec son certificat, vous sera ensuite renvoyé par email.
                            </p>
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-bold text-content-muted">Votre nom complet (tel qu'il figurera sur le {documentLabel} signé)</label>
                        <input value={nom} onChange={(e) => setNom(e.target.value)} className={champ} placeholder="Prénom et nom" autoComplete="name" />
                    </div>

                    <label className="flex items-start gap-2.5 cursor-pointer p-3 rounded-xl border border-border hover:border-brand-500/40 transition-colors">
                        <input type="checkbox" checked={mentionOk} onChange={(e) => setMentionOk(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-brand-600" />
                        <span className="text-xs text-content leading-relaxed">
                            {ctx.mention}
                            {ctx.cgv && (
                                <> <a href={ctx.cgv.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="font-bold underline inline-flex items-center gap-0.5 whitespace-nowrap">Lire les CGV <ExternalLink size={10} /></a></>
                            )}
                        </span>
                    </label>

                    {avecRetractation && (
                        <div className="space-y-2">
                            <div className="flex items-start gap-2 text-[11px] text-content-muted leading-relaxed">
                                <Info size={13} className="shrink-0 mt-0.5" />
                                <p>
                                    Vous disposez de <span className="font-bold text-content">{ctx.retractation_days} jours</span> à compter de la signature pour vous rétracter
                                    {ctx.particulier ? ' (art. L221-18 du Code de la consommation)' : ''}, par simple courrier ou email à l'établissement, sans avoir à vous justifier.
                                </p>
                            </div>
                            <label className="flex items-start gap-2.5 cursor-pointer">
                                <input type="checkbox" checked={executionAnticipee} onChange={(e) => setExecutionAnticipee(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-brand-600" />
                                <span className="text-[11px] text-content-muted leading-relaxed">
                                    <span className="font-bold">Facultatif —</span> je souhaite que la prestation commence avant la fin de ce délai. Si elle est
                                    entièrement réalisée avant, je ne pourrai plus me rétracter (art. L221-25 et L221-28).
                                </span>
                            </label>
                        </div>
                    )}

                    <button type="button" onClick={demanderCode} disabled={!pret || busy}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-black disabled:opacity-50">
                        {busy ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Signer — recevoir mon code par email
                    </button>
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="flex items-start gap-2.5 p-3 rounded-xl bg-surface-soft border border-border">
                        <Mail size={18} className="text-brand-600 dark:text-brand-400 shrink-0 mt-0.5" />
                        <p className="text-xs text-content-muted leading-relaxed">
                            Nous venons d'envoyer un code à <span className="font-bold text-content">{codeInfo?.email}</span>. Saisissez-le ci-dessous pour signer.
                            Il est valable 15 minutes — pensez à regarder dans vos spams.
                        </p>
                    </div>
                    <div>
                        <label className="text-xs font-bold text-content-muted flex items-center gap-1"><KeyRound size={12} /> Code à 6 chiffres</label>
                        <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                            className={`${champ} text-center text-2xl font-black tracking-[0.5em]`} placeholder="______" />
                    </div>
                    <button type="button" onClick={signer} disabled={code.length !== 6 || busy}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-black disabled:opacity-50">
                        {busy ? <Loader2 size={15} className="animate-spin" /> : <PenLine size={15} />} Signer le {documentLabel}
                    </button>
                    <div className="flex items-center justify-between text-[11px] text-content-muted">
                        <button type="button" onClick={() => { setEtape('mentions'); setErreur(''); }} className="underline hover:text-content">Revenir en arrière</button>
                        <button type="button" onClick={demanderCode} disabled={renvoiDans > 0 || busy} className="underline hover:text-content disabled:no-underline disabled:opacity-60">
                            {renvoiDans > 0 ? `Renvoyer le code dans ${renvoiDans} s` : 'Renvoyer le code'}
                        </button>
                    </div>
                </div>
            )}

            {erreur && <p className="text-xs text-red-500 font-semibold">{erreur}</p>}
        </div>
    );
};
